/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import Table, { TableProps } from 'antd/lib/table';
import moment from 'moment';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, message, Result, Spin } from 'antd';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import ResizeObserver from 'resize-observer-polyfill';
import BPromise from 'bluebird';
import { debounce } from 'lodash';
import AboveNav from '~components/organisms/AboveNav/AboveNav';
import { useStyles } from '~src/hooks/useStyles';
import { fadeInKeyframes } from '~src/styles/animations';
import IconButton from '~components/_common/IconButton/IconButton';
import { styleFn } from './PublicShare.style';
import {
  mapDbFileVersionToTableEntry,
  ShareTableEntry,
  sortByType,
} from './PublicShare.util';
import { sortByNumberProp } from '~src/utils/sort';
import { formatBytes } from '~src/utils/formatBytes';
import { useURLQueryParams } from '~src/hooks/useURLQueryParams';
import {
  loadPublicShareLink,
  loadShare,
  loadSharedFiles,
} from '~src/services/api/shares';
import { DbFileVersion } from '~src/types/db';
import { downloadFileVersions, saveFromLink } from '~src/utils/download';
import { appLog } from '~src/utils/log';
import LargeFilesContext, {
  LargeFilesCtx,
} from '~src/contexts/LargeFilesContext';
import { LargeFilesModal } from '~components/popups-modals/LargeFiles.modal';
import { MockLeftNavigation } from './PublicShare.extra';
import { filterByQuery } from '~src/utils/searchFilters';
import { SearchContext } from '~src/contexts/SearchContext';
import { formatDate } from '~src/utils/formatDate';
import { DownloadingState, useStore } from '~src/store';
import { FileProgressPanelDownload } from '~components/molecules/FileProgressPanelDownload/FileProgressPanelDownload';

const sortByCreatedAt = sortByNumberProp('createdAt');

/** /share Public Share Route
 *
 * This page is custom as it is used outside /projects main authenticated routing tree
 * It can be accessed without login and has very limited functionality
 */
const PublicShare = () => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const { shareId } = useParams<{ shareId: string }>();

  const shareKey = useURLQueryParams().get('key');

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const [bottomViewHeight, setBottomViewHeight] = useState(0);

  const headerDivRef = useRef<HTMLDivElement>(null);
  const buttonsDivRef = useRef<HTMLDivElement>(null);

  const resizeObserverRef = useRef(
    new ResizeObserver(
      debounce(([resizeEntry]) => {
        // update `bottomViewHeight` based on "zoneDiv" height
        setBottomViewHeight(resizeEntry.contentRect.height);
      }, 50)
    )
  );

  // attach resize observer on zoneDiv
  useEffect(() => {
    const div = buttonsDivRef.current;

    if (div) {
      const resizeObserver = resizeObserverRef.current; // ResizeObserver instance
      resizeObserver.observe(div); // add listener

      // cleanup -> remove listener
      return () => resizeObserver.unobserve(div);
    }
  }, [selectedKeys.length]);

  const shareQuery = useQuery(
    ['publicShare', { id: shareId, key: shareKey }],
    loadShare,
    {
      enabled: !!shareKey && !!shareKey,
      refetchOnWindowFocus: false,
    }
  );

  const sharedFilesQuery = useQuery(
    [
      'publicShare-files',
      {
        key: shareKey,
        shareId,
        withLink: false,
      },
    ],
    loadSharedFiles,
    {
      enabled: shareQuery.data,
      refetchOnWindowFocus: false,
      onError: (sharedFilesQueryErr) => {
        appLog('sharedFilesQueryErr', sharedFilesQueryErr);
        message.error(intl.formatMessage({ id: 'active' }));
      },
    }
  );
  const searchContext = useContext(SearchContext);

  // Have to render provider of this context, to be used by `LargeFilesModal`
  const [largeFilesContext, setLargeFilesContext] = useState<
    LargeFilesCtx['largeFilesContext']
  >([]);
  const largeFilesChanged = useMemo(
    () => ({ largeFilesContext, setLargeFilesContext }),
    [largeFilesContext, setLargeFilesContext]
  );

  const filesSource = useMemo<ShareTableEntry[]>(
    () => (sharedFilesQuery.data || []).map(mapDbFileVersionToTableEntry),
    [sharedFilesQuery.data]
  );

  const filteredFiles = [
    ...(searchContext.query
      ? filesSource.filter((item) => {
          if (
            filterByQuery(item.name, searchContext.query) ||
            filterByQuery(item.description, searchContext.query) ||
            filterByQuery(item.type.icon, searchContext.query)
          ) {
            return item;
          }
          return null;
        })
      : filesSource),
  ];

  const { changeDownloadingActive, changeDownloadingState } = useStore();

  const columns = useMemo<TableProps<ShareTableEntry>['columns']>(
    () => [
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 75,
        align: 'center',
        render: (type, record) => (
          <IconButton
            iconStyle={type.iconStyle}
            icon={type.icon}
            customStyle={styles.typeIcon}
            color={type.color}
            tooltip={intl.formatMessage(
              { id: type.icon },
              { type: record.extension?.toUpperCase() }
            )}
            backgroundOff
          />
        ),
        sorter: sortByType,
      },
      {
        title: intl.formatMessage({ id: 'name' }),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        width: 210,
        ellipsis: true,
      },
      {
        title: intl.formatMessage({ id: 'size' }),
        dataIndex: 'size',
        width: 90,
        render: (size) => formatBytes(size, 1),
      },
      {
        title: intl.formatMessage({ id: 'uploaded-date' }),
        dataIndex: 'createdAt',
        sorter: sortByCreatedAt,
        width: 145,
        render: (createdAt) => formatDate(createdAt, 'short'),
      },
    ],
    [intl, styles.typeIcon]
  );

  // if no share key, render error screen
  if (!shareKey || !shareId) {
    return (
      <div css={styles.fullScreen}>
        <Result
          status="error"
          title={intl.formatMessage({ id: 'public-share-invalid-link' })}
          subTitle={intl.formatMessage({
            id: 'public-share-invalid-link-info',
          })}
        />
      </div>
    );
  }

  // if share query error, render error screen
  if (shareQuery.error) {
    return (
      <div css={styles.fullScreen}>
        <Result
          status="error"
          title={intl.formatMessage({ id: 'generic-error' })}
          subTitle={intl.formatMessage({ id: 'public-share-load-failed' })}
        />
      </div>
    );
  }

  if (!shareQuery.data) {
    return (
      <div css={styles.fullScreen}>
        <Spin size="large" />
      </div>
    );
  }

  const toggleSelectedRecord = (key: string) => {
    setSelectedKeys((prevSelectedKeys) => {
      const nextSelectedKeys = [...prevSelectedKeys];
      const currentIndex = nextSelectedKeys.indexOf(key);

      if (currentIndex > -1) {
        nextSelectedKeys.splice(currentIndex, 1);
      } else {
        nextSelectedKeys.push(key);
      }

      return nextSelectedKeys;
    });
  };

  interface HandleDownloadParams {
    all?: boolean;
    selectedFileKeys?: typeof selectedKeys;
  }

  const handleDownload = async ({
    all,
    selectedFileKeys,
  }: HandleDownloadParams) => {
    if ((!all && !selectedFileKeys) || !sharedFilesQuery.data) {
      return message.error('No files');
    }

    let files: DbFileVersion[] = [];

    if (all) {
      files = [...sharedFilesQuery.data];
    } else if (selectedFileKeys) {
      files = selectedFileKeys
        .map((key) => sharedFilesQuery.data?.find((file) => file.id === key))
        .filter(Boolean) as DbFileVersion[];
    }

    await BPromise.map(files, async (file) => {
      file.location = await loadPublicShareLink({
        shareId,
        fileId: file.id,
        key: shareKey,
      });
    });

    if (!files.length) {
      return message.error('No files');
    }

    if (files.length === 1) {
      message.info(intl.formatMessage({ id: 'download-starting' }));
      saveFromLink(files[0].location, files[0].name);
      return;
    }

    // const hidePrepMsg = message.loading(
    //   intl.formatMessage({ id: 'download-preparing' }),
    //   0
    // );
    appLog('Activating download...');
    changeDownloadingActive(true);
    appLog('Changing downloading state...');
    changeDownloadingState(DownloadingState.PREPARING);
    appLog('Download state changed:', DownloadingState.PREPARING);
    downloadFileVersions({
      fileVersions: files,
      onDownloadStarted: () => {
        // hidePrepMsg();
        // setTimeout(
        //   () => message.info(intl.formatMessage({ id: 'download-starting' })),
        //   400
        // );
      },
      onLargeFiles: (largeFiles) => setLargeFilesContext(largeFiles),
    }).catch((err) => {
      appLog('downloadFileVersions -> err', err);
      // hidePrepMsg && hidePrepMsg();
      message.error(intl.formatMessage({ id: 'file-download-failed' }));
    });
  };

  // 142px is topBar + folderNameBar + tableHeader + scrollbar
  const tableHeight = `calc(100vh - 121px - ${
    headerDivRef.current ? headerDivRef.current.clientHeight : '0'
  }px - ${selectedKeys.length > 0 ? bottomViewHeight : 0}px)`;

  return (
    <div
      css={{
        height: '100%',
        animation: `${fadeInKeyframes} 0.4s ease-in`,
      }}>
      <AboveNav publicPage={true} />

      {/* Progress Panel Download*/}
      <FileProgressPanelDownload />

      <div
        css={{
          display: 'flex',
          width: '100%',
          height: 'calc(100vh - 50px)',
        }}>
        <LargeFilesContext.Provider value={largeFilesChanged}>
          <div css={styles.leftContainer}>
            <MockLeftNavigation />
          </div>

          <div css={styles.rightContainer}>
            <div css={styles.rightHeader}>
              <div css={styles.headline}>
                <div css={styles.headerPadding} ref={headerDivRef}>
                  {shareQuery.data.description}{' '}
                </div>
                {intl.formatMessage({ id: 'expires' })}
                &nbsp;&nbsp;
                {formatDate(shareQuery.data.expires_at, 'long')}
              </div>

              <div css={styles.headline}>
                <IconButton
                  icon={['far', 'file-download']}
                  customStyle={styles.headerIcon}
                  tooltip={intl.formatMessage({ id: 'download-all' })}
                  click={() => handleDownload({ all: true })}
                />
              </div>
            </div>

            <Table<ShareTableEntry>
              showSorterTooltip={false}
              css={[
                styles.table,
                {
                  '.ant-table-body': {
                    height: tableHeight,
                  },
                },
              ]}
              className={`main-content-window ${
                window.sessionStorage.getItem('appState') === 'timeMachine' &&
                'timeMachine-table'
              }`}
              rowClassName={(record) => {
                let className = '';
                if (
                  window.sessionStorage.getItem('appState') === 'timeMachine'
                ) {
                  className += 'timeMachine-row-table' + ' ';
                }
                return className;
              }}
              pagination={false}
              bordered
              scroll={{
                x: 700,
                y: tableHeight,
              }}
              columns={columns}
              dataSource={filteredFiles}
              loading={sharedFilesQuery.isLoading || sharedFilesQuery.isIdle}
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys: selectedKeys,
                onChange: (keys) => setSelectedKeys(keys as string[]),
              }}
              onRow={(record) => ({
                onClick: () => toggleSelectedRecord(record.key),
                onDoubleClick: () =>
                  handleDownload({ selectedFileKeys: [record.key] }),
              })}
            />

            {selectedKeys.length > 0 && (
              <div css={styles.buttonsContainer} ref={buttonsDivRef}>
                <Button
                  css={styles.button}
                  type="primary"
                  onClick={() =>
                    handleDownload({ selectedFileKeys: selectedKeys })
                  }>
                  {intl.formatMessage({ id: 'download-selected' })}
                </Button>

                {/* show "DownloadAll" button if there are multiple files   */}
                {(sharedFilesQuery.data?.length || 0) > 1 && (
                  <Button
                    css={styles.button}
                    onClick={() => handleDownload({ all: true })}>
                    {intl.formatMessage({ id: 'download-all' })}
                  </Button>
                )}
              </div>
            )}
          </div>

          {largeFilesContext.length > 0 && <LargeFilesModal />}
        </LargeFilesContext.Provider>
      </div>
    </div>
  );
};

export default PublicShare;
